/**
 * LiveClassTopBar
 * ---------------
 * The host's live-class bar: who has joined, and the controls.
 *
 * Students no longer get a banner here — a live class shows them the
 * full-screen LiveClassJoinScreen instead.
 *
 * This also absorbs the old ActiveSessionCodeBadge (which was fixed top-right
 * and would collide with this bar).
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUsers, FaCopy, FaBolt, FaTachometerAlt, FaStop, FaChalkboardTeacher } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useClassSession } from '../../contexts/ClassSessionContext';
import { openLaunchedLessonAsHost } from '../../lib/classroom/openLaunchedLessonAsHost';

const HOST_ROLES = ['teacher', 'partner', 'principal', 'school', 'admin', 'superadmin'];

export const LiveClassTopBar = () => {
  const { profile } = useAuth();
  const {
    activeSession,
    activeSessionId,
    progressList,
    forceStudentsToLesson,
    endSession,
  } = useClassSession();
  const navigate = useNavigate();
  const [forcing, setForcing] = useState(false);
  const [ending, setEnding] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [opening, setOpening] = useState(false);

  const isHost = HOST_ROLES.includes(profile?.role ?? '');

  // ---- Host bar: live session, who has joined, and the controls -------------
  if (isHost) {
    if (!activeSessionId || !activeSession || activeSession.status === 'ended') return null;

    const joinedCount = progressList.length;
    const readyCount = progressList.filter((p) => p.lesson_ready).length;
    const lessonLive = Boolean(activeSession.launched_lesson || activeSession.launched_scene);
    const dashboardPath =
      profile?.role === 'partner' ? '/dashboard/partner'
        : profile?.role === 'principal' ? '/dashboard/principal'
        : profile?.role === 'school' ? '/dashboard/school'
        : profile?.role === 'teacher' ? '/dashboard/teacher'
        : '/studio/content';

    const copyHostCode = async () => {
      try {
        await navigator.clipboard.writeText(activeSession.session_code);
        toast.success('Class code copied');
      } catch {
        toast.error('Could not copy class code');
      }
    };

    return (
      <div
        role="status"
        className="fixed inset-x-0 top-0 z-[60] border-b border-emerald-400/30 bg-card/95 shadow-lg backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-3 py-2 sm:gap-3 sm:px-6">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-400/10 text-emerald-500">
            <FaUsers className="h-3.5 w-3.5" />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Your class is live
            </p>
            <p className="truncate text-sm font-medium text-foreground">
              <span className="tabular-nums font-semibold">{joinedCount}</span>{' '}
              {joinedCount === 1 ? 'student has' : 'students have'} joined
              {joinedCount > 0 && (
                <span className="text-muted-foreground"> · {readyCount} ready</span>
              )}
              {lessonLive ? ' · lesson launched' : ' · no lesson launched yet'}
            </p>
          </div>

          <button
            type="button"
            onClick={copyHostCode}
            className="hidden items-center gap-2 rounded-md px-2 py-1.5 font-mono text-sm font-bold tracking-[0.18em] text-foreground transition hover:bg-muted sm:flex"
            aria-label="Copy class session code"
          >
            {activeSession.session_code}
            <FaCopy className="h-3 w-3 text-muted-foreground" />
          </button>

          <button
            type="button"
            disabled={!lessonLive || forcing}
            title={
              lessonLive
                ? 'Pull every joined student into the lesson now'
                : 'Launch a lesson first'
            }
            onClick={async () => {
              setForcing(true);
              try {
                const ok = await forceStudentsToLesson();
                toast[ok ? 'success' : 'error'](
                  ok ? 'Bringing everyone into the lesson…' : 'Could not reach the class.'
                );
              } finally {
                setForcing(false);
              }
            }}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
          >
            <FaBolt className="h-3 w-3" />
            {forcing ? 'Bringing in…' : 'Bring everyone in'}
          </button>

          {/* The way into the lesson, from wherever the teacher happens to be.
              Launching used to march them to /dashboard/teacher for this one
              button; the bar is already on every page, so the button lives here
              and the launch leaves them where they were. */}
          <button
            type="button"
            disabled={!lessonLive || opening}
            title={
              lessonLive
                ? 'Open the launched lesson so you can direct the class view'
                : 'Launch a lesson first'
            }
            onClick={async () => {
              setOpening(true);
              try {
                const result = await openLaunchedLessonAsHost({
                  launched: activeSession.launched_lesson,
                  sessionId: activeSessionId,
                  navigate,
                });
                if (!result.ok) {
                  toast.error(
                    result.reason === 'tour-missing'
                      ? '360° tour not found.'
                      : 'Could not open lesson. Try again.'
                  );
                }
              } finally {
                setOpening(false);
              }
            }}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-400/20 disabled:opacity-50 dark:text-emerald-300"
          >
            <FaChalkboardTeacher className="h-3 w-3" />
            {opening ? 'Opening…' : 'Open lesson'}
          </button>

          <Link
            to={dashboardPath}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <FaTachometerAlt className="h-3 w-3" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>

          {/* Two-step confirm, in-app.
              This used to gate on window.confirm(). A browser that suppresses dialogs —
              which every major one does once the user ticks "prevent this page from
              creating additional dialogs", and which also happens in embedded contexts —
              returns false from confirm(), so the handler returned immediately: no write,
              no toast, no error. Clicking End did nothing at all, with nothing to see in
              the console. The result is also honoured now; it used to toast success
              unconditionally, so a rejected write still read as "Class session ended." */}
          <button
            type="button"
            disabled={ending}
            title={confirmEnd ? 'Click again to end for everyone' : 'End this class session for everyone'}
            onClick={async () => {
              if (!confirmEnd) {
                setConfirmEnd(true);
                window.setTimeout(() => setConfirmEnd(false), 4000);
                return;
              }
              setConfirmEnd(false);
              setEnding(true);
              try {
                const ok = await endSession();
                toast[ok ? 'success' : 'error'](
                  ok ? 'Class session ended.' : 'Could not end the session. It is still live — see the console for why.'
                );
              } finally {
                setEnding(false);
              }
            }}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
              confirmEnd
                ? 'border-destructive bg-destructive/15 text-destructive'
                : 'border-destructive/40 text-destructive hover:bg-destructive/10'
            }`}
          >
            <FaStop className="h-3 w-3" />
            <span className="hidden sm:inline">
              {ending ? 'Ending…' : confirmEnd ? 'Confirm end?' : 'End'}
            </span>
          </button>
        </div>
      </div>
    );
  }

  // Students see LiveClassJoinScreen instead: a full-screen card with one
  // button, rather than a banner over every page.
  return null;
};
